import type { ReferenceDataAgent } from "@/types/reference-data";

const ROOT = "/admin/reference-data/agents";

export function agentPath(agentKey: string): string {
  return `${ROOT}/${encodeURIComponent(agentKey)}`;
}

export function configTypePath(agentKey: string, typeKey: string): string {
  return `${agentPath(agentKey)}/${encodeURIComponent(typeKey)}`;
}

/** Caminho da tela do tipo, achando o agente dele; undefined sem dado, sem tipo ou com tipo que
 *  nenhum agente declara.
 *  Com `tab`, o caminho abre direto nessa aba (`releases`, `upload` ou uma tabela). */
export function configTypePathFor(
  agents: ReferenceDataAgent[] | undefined,
  typeKey: string | null | undefined,
  tab?: string,
): string | undefined {
  if (!agents || !typeKey) return undefined;
  const agent = agents.find((a) => a.config_types.some((c) => c.key === typeKey));
  if (!agent) return undefined;
  const path = configTypePath(agent.key, typeKey);
  return tab ? `${path}?tab=${encodeURIComponent(tab)}` : path;
}

/** Campo numérico do relatório do pacote que vale um número na tela. */
export interface ConfigTypeStatField {
  key: string;
  label: string;
  accent?: "ok" | "gold";
}

/** O que a tela de upload do tipo diz e aceita: um registro por tipo de configuração. */
export interface ConfigTypeUi {
  /** Extensões aceitas no arquivo, com o ponto e em minúsculas. */
  acceptedExtensions: string[];
  uploadTitle: string;
  uploadHelp: string;
  dropLabel: string;
  wrongExtensionMessage: string;
  /** Campos do relatório de cada tabela, na ordem de leitura. */
  statFields: ConfigTypeStatField[];
}

/** Hoje só o SSCUI declara tela própria; os textos são chaves de i18n (o teste de i18n
 *  confere as três línguas). */
export const CONFIG_TYPE_UI: Record<string, ConfigTypeUi> = {
  sscui: {
    acceptedExtensions: [".csv"],
    uploadTitle: "SAP SSCUI List file",
    uploadHelp:
      "Upload SAP's SSCUI_List_EN_XX file as published (semiannual release). It updates the activity catalog and the scope items per activity together, with a new version in each.",
    dropLabel: "Drop the .csv here or click to choose",
    wrongExtensionMessage: "Only the SAP spreadsheet in .csv is accepted.",
    statFields: [
      { key: "row_count", label: "Rows in file" },
      { key: "new_count", label: "New", accent: "ok" },
      { key: "updated_count", label: "Updated", accent: "gold" },
      { key: "removed_count", label: "Removed by SAP", accent: "gold" },
      { key: "kept_curated_count", label: "Curated kept" },
      { key: "pair_count", label: "Scope item pairs" },
      { key: "new_pair_count", label: "New pairs", accent: "ok" },
      { key: "removed_pair_count", label: "Removed pairs", accent: "gold" },
    ],
  },
  depara: {
    acceptedExtensions: [".csv"],
    uploadTitle: "DE-PARA mapping file",
    uploadHelp:
      "Choose the source SAP system and the tab, then upload the CSV with the 5 columns of the example. Each table in the file replaces the fields of that table for that source; tables not in the file stay as they are. Source Table and Source Field accept 1–30 characters: letters, numbers and _ only. Use one table per row.",
    dropLabel: "Drop the .csv here or click to choose",
    wrongExtensionMessage: "Only the SAP spreadsheet in .csv is accepted.",
    statFields: [
      { key: "row_count", label: "Rows in file" },
      { key: "tables_replaced", label: "Tables replaced" },
      { key: "fields_added", label: "New fields", accent: "ok" },
      { key: "fields_removed", label: "Removed fields", accent: "gold" },
      { key: "fields_changed", label: "Changed fields", accent: "gold" },
    ],
  },
};

/** Tipo sem registro próprio: textos neutros, sem contadores específicos. */
export const GENERIC_CONFIG_TYPE_UI: ConfigTypeUi = {
  acceptedExtensions: [".csv"],
  uploadTitle: "Configuration type file",
  uploadHelp:
    "Upload the file as published. It updates every table of the configuration type together, with a new version in each.",
  dropLabel: "Drop the file here or click to choose",
  wrongExtensionMessage: "This file type is not accepted.",
  statFields: [
    { key: "row_count", label: "Rows in file" },
    { key: "new_count", label: "New", accent: "ok" },
    { key: "updated_count", label: "Updated", accent: "gold" },
  ],
};

export function configTypeUi(typeKey: string): ConfigTypeUi {
  return CONFIG_TYPE_UI[typeKey] ?? GENERIC_CONFIG_TYPE_UI;
}

/** Rótulo da release: a da SAP quando há; senão a versão que o pacote gravou. */
export function packageReleaseLabel(pkg: {
  sap_release: string | null;
  runs: { version: string | null }[];
}): string {
  return pkg.sap_release ?? pkg.runs[0]?.version ?? "—";
}

/** A carga inicial do seed: o pacote que existia antes do primeiro upload. */
export function isInitialLoad(pkg: { runs: { report: { source?: string } }[] }): boolean {
  return pkg.runs[0]?.report?.source === "seed";
}

/**
 * Release ativa do tipo: a da SAP; sem ela (DE-PARA, carga inicial), a versão da
 * primeira tabela. `null` só sem nenhuma das duas.
 */
export function activeReleaseLabel(type: {
  active_release: { sap_release: string | null } | null;
  tables: { active_version: string | null }[];
}): string | null {
  return type.active_release?.sap_release ?? type.tables[0]?.active_version ?? null;
}
